import {
  UnifiedMonitoringMessageType,
  UnifiedMonitoringOutboundMessage,
} from '@hakimo-ui/hakimo/types';
import { useHttpClient } from '@hakimo-ui/hakimo/util';
import { useMutation } from 'react-query';

/**
 * Best-effort backup REST call for Unified Monitoring queue SCAN quick-actions,
 * fired alongside the primary /monitoring WebSocket message in case it drops.
 * Not used by the Alarm Group detail page.
 *
 * The URLs are shared with the Remote-Guarding endpoints via `product=scan`, but
 * the gateway parses this raw WS envelope directly for that product value, so
 * the body does not need to match the RG request shapes. Returns null for
 * anything with no backup path.
 */
function getBackupRequest(
  message: UnifiedMonitoringOutboundMessage
): Request | null {
  const body = JSON.stringify(message);
  const headers = { 'Content-Type': 'application/json' };
  switch (message.event_type) {
    case UnifiedMonitoringMessageType.ALARM_SAFE:
      return new Request(
        `/v2/orm/location_alarms/update/${message.alarm_group_id}?product=scan`,
        { method: 'POST', headers, body }
      );
    case UnifiedMonitoringMessageType.ALARM_ESCALATION_OPEN:
      return new Request('/v2/orm/location_incidents/?product=scan', {
        method: 'POST',
        headers,
        body,
      });
    case UnifiedMonitoringMessageType.ALARM_ESCALATION_CLOSE:
      return new Request('/v2/orm/location_incidents/resolve?product=scan', {
        method: 'PUT',
        headers,
        body,
      });
    default:
      return null;
  }
}

/**
 * Backup REST path for Unified-Monitoring SCAN operator actions
 * (Safe / Escalation-open / Escalation-close).
 *
 * These actions are sent over the /monitoring WebSocket, which event-flow
 * relays to SQS and the vision-event-consumer applies to the DB. That WS hop
 * is occasionally dropped, so the console now ALSO POSTs the same payload to
 * the existing Remote-Guarding endpoints with `?product=scan`. The gateway
 * applies the same DB effect idempotently, so firing both paths is safe —
 * whichever lands first wins, the other is a no-op.
 *
 * Best-effort: this is a backup, so callers should fire it without blocking
 * the WS flow and tolerate failures.
 */
export function useScanAlarmActionBackup() {
  const httpClient = useHttpClient();

  return useMutation<void, Error, UnifiedMonitoringOutboundMessage>({
    mutationFn: async (message: UnifiedMonitoringOutboundMessage) => {
      const request = getBackupRequest(message);
      if (!request) {
        return;
      }
      await httpClient(request);
    },
  });
}

export default useScanAlarmActionBackup;
